// src/features/messages/NewDmDialog.tsx — pencil compose flow: pick a person,
// start (or resolve into) a DM with them.
import { useState } from 'react';
import styled from 'styled-components';
import { Dialog, Button } from '@/ui';
import { useMessages, useSession } from '@/hooks';
import { ApiError } from '@/core/lib/apiClient';
import { PersonPicker } from './PersonPicker';
import type { PersonOption } from './PersonPicker';

const Stack = styled.div`display: flex; flex-direction: column; gap: 14px;`;
const ErrorText = styled.p`
  margin: 0; font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.danger};
`;
const ButtonRow = styled.div`display: flex; justify-content: flex-end; gap: 8px;`;
// Not an error — this is the ordinary outcome of messaging someone who does not
// follow you, so it reads as information, not a warning.
const NoticeText = styled.p`
  margin: 0; font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted}; max-width: 46ch;
`;

export interface NewDmDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the conversation id once the DM exists (new or resolved existing). */
  onCreated: (conversationId: string) => void;
}

export function NewDmDialog({ open, onClose, onCreated }: NewDmDialogProps) {
  const { createDm } = useMessages();
  const { currentUser } = useSession();
  const [notAllowed, setNotAllowed] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /**
   * Set only when the thread we just created is awaiting the other side's
   * acceptance — i.e. they do not follow us, so under `dm_policy: everyone`
   * the first message routes to their Requests instead of their inbox.
   *
   * Say it HERE rather than leave it to the thread's "Request sent" banner.
   * That banner is honest but late: it appears after the member has already
   * chosen a person and started composing, and it is worded for a message
   * that has been sent. This is the moment the expectation is actually set —
   * no notification, no unread mark, and nothing delivered until they accept.
   */
  const [awaiting, setAwaiting] = useState<{ id: string; handle: string } | null>(null);

  async function handlePick(person: PersonOption) {
    setNotAllowed(null);
    setFieldError(null);
    setSubmitting(true);
    try {
      const convo = await createDm(person.handle);
      if (convo.awaitingAcceptance === true) {
        setAwaiting({ id: convo.id, handle: person.handle });
        return;
      }
      onCreated(convo.id);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'dm_not_allowed') {
        setNotAllowed("They don't accept direct messages.");
      } else if (e instanceof ApiError && e.code === 'not_found') {
        const handle = typeof e.fields?.handle === 'string' ? e.fields.handle : person.handle;
        setFieldError(`We couldn't find ${handle}.`);
      } else {
        setFieldError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="New message">
      <Stack>
        {awaiting ? (
          <>
            <NoticeText role="status" data-testid="new-dm-awaiting">
              {awaiting.handle} does not follow you, so your message goes to their Requests rather
              than their inbox. They will not get a notification, and it stays unread on their side,
              until they accept it.
            </NoticeText>
            <ButtonRow>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => onCreated(awaiting.id)}>
                Write it anyway
              </Button>
            </ButtonRow>
          </>
        ) : (
          <>
            <PersonPicker
              exclude={currentUser ? [currentUser.handle] : []}
              onPick={(p) => void handlePick(p)}
              error={fieldError}
              onQueryChange={() => setFieldError(null)}
              disabled={submitting}
            />
            {notAllowed && <ErrorText role="alert">{notAllowed}</ErrorText>}
            <ButtonRow>
              <Button variant="ghost" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
            </ButtonRow>
          </>
        )}
      </Stack>
    </Dialog>
  );
}
